import { Step } from '@widget/core';
import { Grid, GridItem } from '@toolbox/components';
import { ContactStep, ThankYouStep, QuestionStep, ValuationStep } from '../presets';
import { FormQuestionStep } from '../presets/FormQuestionStep';
import { renderComponent } from './renderComponent';
import { renderWhen, stringOrUndefined } from './utils';
import { BookBrokerAppointmentOnSiteStep } from '@widget/presets/BookBrokerAppointmentOnSiteStep';
import {
  BookBrokerAppointmentThankYouPage,
  BookSalesAppointmentThankYouPage,
} from '@widget/presets/BookAppointmentThankYouPage';
import { ContactStepWithSlider } from '@widget/presets/ContactStepWithSlider';
import { BookBrokerAppointmentChoiceStep } from '@widget/presets/BookBrokerAppointmentChoiceStep/BookBrokerAppointmentChoiceStep';
import { BookBrokerAppointmentStep } from '@widget/presets/BookBrokerAppointmentStep';
import { BookSalesAppointmentStep } from '@widget/presets/BookSalesAppointmentStep';
import { AddressMultipleFieldsStep } from '@widget/presets/AddressMultipleFieldsStep/AddressMultipleFieldsStep';
import { ThankYouStepV2 } from '@widget/presets/ThankYouStep/ThankYouStepV2';

import type { StepChild } from '@widget/core';
import type {
  AnyStepSchema,
  QuestionStepSchema,
  ContactStepSchema,
  ThankYouStepSchema,
  ValuationStepSchema,
  StepSchema,
  ContactStepWithSliderSchema,
  BookBrokerAppointmentChoiceStepSchema,
  BookBrokerAppointmentStepSchema,
  BookBrokerAppointmentThankYouPageSchema,
  BookSalesAppointmentStepSchema,
  BookSalesAppointmentThankYouPageSchema,
  AddressMultipleFieldsStepSchema,
} from './schemas';
import type { z } from 'zod';
import type { Richtext } from 'storyblok-js-client';

type AnyStepProps = z.infer<typeof AnyStepSchema>;
type StepProps = z.infer<typeof StepSchema>;
type QuestionStepProps = z.infer<typeof QuestionStepSchema>;
type ContactStepProps = z.infer<typeof ContactStepSchema>;
type ContactStepWithSliderProps = z.infer<typeof ContactStepWithSliderSchema>;
type ThankYouStepProps = z.infer<typeof ThankYouStepSchema>;
type ValuationStepProps = z.infer<typeof ValuationStepSchema>;
type BookBrokerAppointmentChoiceStepProps = z.infer<typeof BookBrokerAppointmentChoiceStepSchema>;
type BookBrokerAppointmentStepProps = z.infer<typeof BookBrokerAppointmentStepSchema>;
type BookSalesAppointmentStepProps = z.infer<typeof BookSalesAppointmentStepSchema>;
type BookBrokerAppointmentThankYouPageProps = z.infer<
  typeof BookBrokerAppointmentThankYouPageSchema
>;
type BookSalesAppointmentThankYouPageProps = z.infer<typeof BookSalesAppointmentThankYouPageSchema>;
type AddressMultipleFieldsStepProps = z.infer<typeof AddressMultipleFieldsStepSchema>;

const steps = {
  QuestionStep: (props: QuestionStepProps) => (
    <QuestionStep
      includeGuidanceModal={props.includeGuidanceModal ?? false}
      modalCta={props.modalCta || ''}
      modalTitle={props.modalTitle || ''}
      modalGridSize={Number(props.modalGridSize ?? 2)}
      guidances={props.guidances || []}
      back={props.back ?? undefined}
      hideLiveValuation={!!props.hideLiveValuation}
      gridSize={Number(props.gridSize) || 3}
      valueType={props.valueType || 'string'}
      hideUSP={!!props.hideUSP}
      hiddenFields={props.hiddenFields}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      key={props.id}
      id={props.id}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      title={props.title}
      subTitle={props.subTitle}
      description={props.description}
      name={props.name}
      backButtonText={stringOrUndefined(props.backButtonText)}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      submitButtonText={stringOrUndefined(props.submitButtonText)}
      skipButtonText={stringOrUndefined(props.skipButtonText)}
      options={props.options}
      uspLayout={props.uspLayout || 'horizontal'}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
      analyticsMap={props.analyticsMap || undefined}
      showUSPList={props.showUSPList}
      showProgressBar={props.showProgressBar ?? undefined}
    />
  ),
  ContactStep: (props: ContactStepProps) => (
    <ContactStep
      key={props.id}
      id={props.id}
      renderWhen={renderWhen(props)}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      title={stringOrUndefined(props.title)}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      description={stringOrUndefined(props.description)}
      firstNameLabel={stringOrUndefined(props.firstNameLabel)}
      lastNameLabel={stringOrUndefined(props.lastNameLabel)}
      phoneLabel={stringOrUndefined(props.phoneLabel)}
      phoneTooltip={stringOrUndefined(props.phoneTooltip)}
      emailLabel={stringOrUndefined(props.emailLabel)}
      emailTooltip={stringOrUndefined(props.emailTooltip)}
      buttonText={stringOrUndefined(props.buttonText)}
      buttonSubText={stringOrUndefined(props.buttonSubText)}
      disclaimerText={stringOrUndefined(props.disclaimerText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      uspLayout={props.uspLayout || 'horizontal'}
      newsletterText={stringOrUndefined(props.newsletterText)}
      isNewsletterOptInShown={props.isNewsletterOptInShown}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      analyticsMap={props.analyticsMap}
      showUSPList={props.showUSPList}
    />
  ),
  ContactStepWithSlider: (props: ContactStepWithSliderProps) => (
    <ContactStepWithSlider
      key={props.id}
      id={props.id}
      renderWhen={renderWhen(props)}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      title={stringOrUndefined(props.title)}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      description={stringOrUndefined(props.description)}
      firstNameLabel={stringOrUndefined(props.firstNameLabel)}
      lastNameLabel={stringOrUndefined(props.lastNameLabel)}
      phoneLabel={stringOrUndefined(props.phoneLabel)}
      phoneTooltip={stringOrUndefined(props.phoneTooltip)}
      emailLabel={stringOrUndefined(props.emailLabel)}
      emailTooltip={stringOrUndefined(props.emailTooltip)}
      buttonText={stringOrUndefined(props.buttonText)}
      buttonSubText={stringOrUndefined(props.buttonSubText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      uspLayout={props.uspLayout || 'horizontal'}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      fullWidthLayout={!!props.fullWidthLayout}
      hideHomeIcon={!!props.hideHomeIcon}
      headerType={props.headerType}
      additionalCheckboxes={props.additionalCheckboxes}
      showUSPList={props.showUSPList}
    />
  ),
  ThankYouStep: (props: ThankYouStepProps) => (
    <ThankYouStep
      key={props.id}
      id={props.id}
      renderWhen={renderWhen(props)}
      hideLiveValuation={!!props.hideLiveValuation}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideUSP={!!props.hideUSP}
      hideFooter={props.hideFooter ?? undefined}
      closeWidget={props.closeWidget ?? undefined}
      bookCall={props.bookCall ?? undefined}
      heading={stringOrUndefined(props.heading)}
      title={stringOrUndefined(props.title)}
      description={stringOrUndefined(props.description)}
      buttonText={stringOrUndefined(props.buttonText)}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      bookCallButtonText={stringOrUndefined(props.bookCallButtonText)}
      dataAcquiredText={stringOrUndefined(props.dataAcquiredText)}
      loadingText={stringOrUndefined(props.loadingText)}
      redirectionAlertText={stringOrUndefined(props.redirectionAlertText)}
      resetButtonText={stringOrUndefined(props.resetButtonText)}
      enableAutomaticRedirectionToPropertySearch={
        props.enableAutomaticRedirectionToPropertySearch || []
      }
      propertySearchLink={props.propertySearchLink}
      redirectCustomLink={props.redirectCustomLink?.[0]}
      uspLayout={props.uspLayout || 'horizontal'}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      headerType={props.headerType}
      analyticsMap={props.analyticsMap}
      phoneLink={props.PhoneLink?.[0]}
      userAlreadyHasAppointmentErrorText={stringOrUndefined(
        props.userAlreadyHasAppointmentErrorText,
      )}
    >
      <>{props.children?.map(renderComponent)}</>
    </ThankYouStep>
  ),
  ThankYouStepV2: (props: ThankYouStepProps) => (
    <ThankYouStepV2
      key={props.id}
      id={props.id}
      renderWhen={renderWhen(props)}
      hideUSP={!!props.hideUSP}
      hideFooter={props.hideFooter ?? undefined}
      closeWidget={props.closeWidget ?? undefined}
      heading={stringOrUndefined(props.heading)}
      buttonText={stringOrUndefined(props.buttonText)}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      enableAutomaticRedirectionToPropertySearch={
        props.enableAutomaticRedirectionToPropertySearch || []
      }
      propertySearchLink={props.propertySearchLink}
      uspLayout={props.uspLayout || 'horizontal'}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      headerType={props.headerType}
      analyticsMap={props.analyticsMap}
    >
      <>{props.children?.map(renderComponent)}</>
    </ThankYouStepV2>
  ),
  ValuationStep: (props: ValuationStepProps) => (
    <ValuationStep
      key={props.id}
      id={props.id}
      closeButtonText={stringOrUndefined(props.closeButtonText)}
      hideLiveValuation={props.hideLiveValuation ?? undefined}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideUSP={props.hideUSP ?? undefined}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      title={props.title}
      uspLayout={props.uspLayout || 'horizontal'}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
      analyticsMap={props.analyticsMap}
      loadingText={props.loadingText}
    >
      <Grid justifyContent="space-between" alignItems="center">
        {props.children?.map((child, index) => (
          <GridItem key={index} xs={6} md={3}>
            {renderComponent(child)}
          </GridItem>
        ))}
      </Grid>
    </ValuationStep>
  ),
  Step: (props: StepProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideHeaderText={!!props.hideHeaderText}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={props.uspLayout || 'horizontal'}
      swapUspTextAndTrustIcons={!!props.swapUspTextAndTrustIcons}
      socialProofMessage={props.socialProofMessage}
      headerType={props.headerType}
      analyticsMap={props.analyticsMap}
      showUSPList={props.showUSPList}
      showProgressBar={props.showProgressBar ?? undefined}
    >
      <FormQuestionStep {...props} />
    </Step>
  ),
  BookBrokerAppointmentChoiceStep: (props: BookBrokerAppointmentChoiceStepProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <BookBrokerAppointmentChoiceStep {...props} />
    </Step>
  ),
  BookBrokerAppointmentStep: (props: BookBrokerAppointmentStepProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <BookBrokerAppointmentStep {...props} />
    </Step>
  ),
  BookSalesAppointmentStep: (props: BookSalesAppointmentStepProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <BookSalesAppointmentStep {...props} />
    </Step>
  ),
  BookBrokerAppointmentOnSiteStep: (props: any) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <BookBrokerAppointmentOnSiteStep {...props} />
    </Step>
  ),
  AddressMultipleFieldsStep: (props: AddressMultipleFieldsStepProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <AddressMultipleFieldsStep {...props} />
    </Step>
  ),
  BookBrokerAppointmentThankYouPage: (props: BookBrokerAppointmentThankYouPageProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <BookBrokerAppointmentThankYouPage {...props} />
    </Step>
  ),
  BookSalesAppointmentThankYouPage: (props: BookSalesAppointmentThankYouPageProps) => (
    <Step
      key={props.id}
      id={props.id}
      hideLiveValuation={!!props.hideLiveValuation}
      hideUSP={!!props.hideUSP}
      disableAutofocus={!!props.disableAutofocus}
      disableLocalizedUsp={!!props.disableLocalizedUsp}
      hideFooter={props.hideFooter ?? undefined}
      nextStep={stringOrUndefined(props.nextStep)}
      renderWhen={renderWhen(props)}
      uspLayout={'horizontal'}
      analyticsMap={props.analyticsMap}
      headerType={props.headerType}
      socialProofMessage={props.socialProofMessage}
    >
      <BookSalesAppointmentThankYouPage {...props} />
    </Step>
  ),
};

export function renderStep(props: AnyStepProps): StepChild {
  const step = steps[props.component]?.(props as never);
  return step || <></>;
}
